import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { TerminalPanel } from '@/components/terminal/TerminalPanel';
import { LogsPanel } from '@/components/terminal/LogsPanel';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { projectsApi } from '@/api/projects';
import type { Project } from '@/types';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  id: string;
  timestamp: Date;
  level: LogLevel;
  message: string;
  source?: string;
  metadata?: Record<string, unknown>;
}

function parseLogLevel(message: string): LogLevel {
  const lowerMessage = message.toLowerCase();
  if (lowerMessage.includes('error') || lowerMessage.includes('exception') || lowerMessage.includes('fail')) {
    return 'error';
  }
  if (lowerMessage.includes('warn')) {
    return 'warn';
  }
  if (lowerMessage.includes('debug') || lowerMessage.includes('trace')) {
    return 'debug';
  }
  return 'info';
}

interface ApiLogEntry {
  timestamp: string;
  stream: string;
  message: string;
}

function transformApiLogs(apiLogs: ApiLogEntry[], serviceName: string): LogEntry[] {
  return apiLogs.map((log, index) => ({
    id: `log-${index}-${Date.now()}`,
    timestamp: new Date(log.timestamp),
    level: parseLogLevel(log.message),
    message: log.message,
    source: serviceName || log.stream,
  }));
}

export function ProjectTerminalPage() {
  const { project } = useOutletContext<{ project: Project }>();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await projectsApi.getLogs(project.slug, 200);

      // Handle the API response format
      if (response && response.logs && Array.isArray(response.logs)) {
        const transformedLogs = transformApiLogs(response.logs, response.serviceName);
        setLogs(transformedLogs);
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      // Show error as a log entry only if we don't have any logs yet
      if (logs.length === 0) {
        setLogs([{
          id: 'error-1',
          timestamp: new Date(),
          level: 'info',
          message: 'No logs available. Start the project to see container logs.',
          source: 'system',
        }]);
      }
    } finally {
      setLoading(false);
    }
  }, [project.slug, logs.length]);

  useEffect(() => {
    fetchLogs();

    // Poll for new logs every 5 seconds
    const interval = setInterval(fetchLogs, 5000);
    return () => clearInterval(interval);
  }, [project.slug]); // Only depend on slug, not fetchLogs to avoid infinite loop

  const handleClearLogs = () => {
    setLogs([]);
  };

  const handleExportLogs = () => {
    const content = logs
      .map(
        (log) =>
          `[${log.timestamp.toISOString()}] [${log.level.toUpperCase()}] [${log.source || 'app'}] ${log.message}`
      )
      .join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${project.slug}-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full flex flex-col">
      <Tabs defaultValue="terminal" className="h-full flex flex-col">
        <div className="px-4 border-b border-gray-200 dark:border-dark-700">
          <TabsList className="bg-transparent p-0 h-auto gap-0">
            <TabsTrigger
              value="terminal"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary-600"
            >
              Terminal
            </TabsTrigger>
            <TabsTrigger
              value="logs"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary-600"
            >
              Logs {logs.length > 0 && `(${logs.length})`}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="terminal" className="flex-1 m-0">
          <TerminalPanel projectSlug={project.slug} className="h-full" />
        </TabsContent>

        <TabsContent value="logs" className="flex-1 m-0">
          <LogsPanel
            logs={logs}
            onClear={handleClearLogs}
            onExport={handleExportLogs}
            className="h-full"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
